import {
  CreateProviderLocationMappingInput,
  ProviderLocationMapping,
  ProviderLocationMappingSearch,
  SortDirectionEnum,
  UpdateProviderLocationMappingValuesInput,
} from '@/models/gen/graphql';
import { ConnectionDetails, QueryInputType, onEnter, queryInput, stringify } from '@/utils';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import SettingsPageGroup, { SettingsPageGroupProps } from '@/features/Settings/components/SettingsPageGroup';

import ClientDropdown from '@/components/ClientDropdown';
import ClientMappingsTable from '@/features/Settings/components/pages/ClientMappings/ClientMappingsTable';
import { ComplexFormItem } from '@/hooks/useComplexForm';
import { ComplexTableRef } from '@/components/ComplexTable';
import FormField from '@/components/FormField';
import { GraphApiResponse } from '@/api/core';
import { OnChange } from '@/hooks/useOnChange';
import PageInfo from '@/components/PageInfo';
import { useCreateProviderLocationMappingBulk } from '@/features/Settings/api/services/providerLocationMappings/createProviderLocationMappingBulk';
import { useDeleteProviderLocationMappingBulk } from '@/features/Settings/api/services/providerLocationMappings/deleteProviderLocationMappingBulk';
import { useSearchProviderLocationMappings } from '@/features/Settings/api/services/providerLocationMappings/searchProviderLocationMappings';
import { useUpdateProviderLocationMappingBulk } from '@/features/Settings/api/services/providerLocationMappings/updateProviderLocationMappingBulk';

const TITLE = 'Client Mappings';
const ICON = <i className="sv sv-earth" />;
const HEADER = { title: TITLE, icon: ICON };

type ClientMappingsFilters = {
  providerId: string;
};
type ClientMappingsState = {
  search: string;
  sorting: { column: string; direction: string };
  count: number;
  valid: boolean;
  dirty: boolean;
};
const initClientMappingsFilters: ClientMappingsFilters = {
  providerId: undefined,
};
const initClientMappingsState: ClientMappingsState = {
  search: '',
  sorting: { column: undefined, direction: undefined },
  count: 0,
  valid: true,
  dirty: false,
};
const ClientMappings = (): ReactNode => {
  const [{ data: { rows: data = [] } = {}, loading: loadingMappings }, { refetch: getMappings }] = useSearchProviderLocationMappings();
  const [{ loading: creatingMappings }, { fetch: createMappings }] = useCreateProviderLocationMappingBulk();
  const [{ loading: updatingMappings }, { fetch: updateMappings }] = useUpdateProviderLocationMappingBulk();
  const [{ loading: deletingMappings }, { fetch: deleteMappings }] = useDeleteProviderLocationMappingBulk();

  const [state, setState] = useState<ClientMappingsState>({ ...initClientMappingsState });
  const { count, search, valid, dirty } = state;

  const loading = useMemo(
    (): boolean => loadingMappings || creatingMappings || updatingMappings || deletingMappings,
    [loadingMappings, creatingMappings, updatingMappings, deletingMappings]
  );

  const complexTableRef = useRef<ComplexTableRef>(null);

  const onChange = useCallback(async (): Promise<void> => {
    if (complexTableRef?.current?.deletions?.length) {
      try {
        const deletePayload = complexTableRef?.current?.deletions?.flatMap(
          (item: ComplexFormItem<ProviderLocationMapping>): string[] => item?.id
        );
        await deleteMappings(deletePayload);
        complexTableRef?.current?.setDeletions([]);
        getMappings();
      } catch (err) {
        console.error(err);
      }
    }
    setState(
      (current: ClientMappingsState): ClientMappingsState => ({
        ...current,
        count: complexTableRef?.current?.items.filter((item: ComplexFormItem<ProviderLocationMapping>): boolean => !!item.data).length,
        valid: complexTableRef?.current?.isValid,
        dirty: complexTableRef?.current?.isDirty,
      })
    );
  }, [deleteMappings, getMappings]);

  const onSubmit = useCallback(
    async (filters?: ClientMappingsFilters): Promise<ConnectionDetails<ProviderLocationMapping>> => {
      const query: ProviderLocationMappingSearch = {
        createdAt: [queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Asc, 0)],
      };
      if (filters?.providerId) query.providerId = queryInput(filters.providerId);

      return getMappings([query]);
    },
    [getMappings]
  );

  const onSave = useCallback(async (): Promise<void> => {
    const transaction = [];
    if (complexTableRef?.current?.additions?.length) {
      transaction.push(async (): Promise<ProviderLocationMapping[]> => {
        const result = await createMappings(
          complexTableRef?.current?.additions?.map(
            (item: ComplexFormItem<ProviderLocationMapping>): CreateProviderLocationMappingInput => item?.data
          )
        );
        return result;
      });
    }
    if (complexTableRef?.current?.updates?.length) {
      transaction.push(async (): Promise<GraphApiResponse> => {
        const updatePayload = complexTableRef?.current?.updates?.map(
          (item: ComplexFormItem<ProviderLocationMapping>): UpdateProviderLocationMappingValuesInput & { id: string } => ({
            id: item?.id,
            ...Object.entries(item?.data).reduce(
              (acc: Record<string, unknown[]>, [key, value]: [string, unknown]): Record<string, unknown[]> => {
                acc[key] = [value];
                return acc;
              },
              {}
            ),
          })
        );
        const result = await updateMappings(updatePayload);
        return result;
      });
    }

    if (transaction.length)
      try {
        await Promise.all(transaction.map((fn) => fn()));
        complexTableRef?.current?.onReset();
        getMappings();
      } catch (err) {
        console.error(err.message || err);
      }
  }, [createMappings, updateMappings, getMappings]);

  const footerProps = useMemo(
    (): { onAdd?: false | (() => void); onCancel?: false | (() => void); onSave?: false | (() => void) } => ({
      onAdd: !loading ? (): void => complexTableRef?.current?.onAdd() : false,
      onCancel: dirty ? (): void => complexTableRef?.current?.onReset() : false,
      onSave: dirty && valid ? onSave : false,
    }),
    [dirty, valid, onSave, loading]
  );

  const filterProps = useMemo(
    (): SettingsPageGroupProps['filters'] => ({
      name: 'clientMappingsFilters',
      value: initClientMappingsFilters,
      onSubmit,
      onReset: () => {
        setState((current: ClientMappingsState): ClientMappingsState => ({ ...current, search: '' }));
        return {};
      },
      submitOnMount: true,
      primary: ({ values, onChange }: { values: ClientMappingsFilters; onChange: OnChange }): JSX.Element => {
        const { providerId } = values;
        return (
          <>
            <ClientDropdown value={providerId} onChange={(value: string): void => onChange({ target: { name: 'providerId', value } })} />
          </>
        );
      },
      controls: ({ values, onChange }): JSX.Element => {
        const { search } = values;
        return (
          <>
            <FormField
              prepend={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={search || ''}
              onChange={onChange}
              onBlur={(): void => setState((current: ClientMappingsState): ClientMappingsState => ({ ...current, search }))}
              onKeyDown={onEnter((): void => {
                setState((current: ClientMappingsState): ClientMappingsState => ({ ...current, search }));
              })}
              placeholder="Search"
              condensed
            />
          </>
        );
      },
    }),
    [onSubmit]
  );

  const filter = useCallback(
    (item: ComplexFormItem<ProviderLocationMapping>): boolean =>
      !search ||
      stringify([item.data?.providerId, item.data?.contractId, item.data?.puLocationId, item.data?.doLocationId])
        .toLowerCase()
        .includes(search?.toLowerCase()),
    [search]
  );

  return (
    <SettingsPageGroup header={HEADER} footer={footerProps} filters={filterProps}>
      <PageInfo>Client Mappings: {count}</PageInfo>
      <ClientMappingsTable data={data} onChange={onChange} options={{ loading, filter }} ref={complexTableRef} />
    </SettingsPageGroup>
  );
};

export default ClientMappings;
